import { useSeoApi } from '@/api/sdk-api';
import { useCheckUserApiKeysMutation, useLazyGetUserApiKeysQuery } from '@/redux/api/apiKeys-api';
import {
  useLazyGetCardDataQuery,
  useLazyGetCardDataWithApiKeyQuery,
} from '@/redux/api/card-analyze-api';
import { useLazyGetUserMeQuery } from '@/redux/api/user-me-api';
import { setApiKeys } from '@/redux/slices/gpt-slice';
import { selectUser, setUser } from '@/redux/slices/user-slice';
import { useDispatch, useSelector } from 'react-redux';

export interface RefreshOptions {
  refetchApiKeys?: boolean;
  refetchCard?: boolean;
  productId?: string;
  isCorrectApiKeys?: boolean;
}

export const useRefreshUserDataBasics = (): ((options?: RefreshOptions) => Promise<void>) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [fetchMe] = useLazyGetUserMeQuery();
  const [fetchApiKeys] = useLazyGetUserApiKeysQuery();
  const [fetchCardData] = useLazyGetCardDataQuery();
  const [fetchCardDataWithApiKey] = useLazyGetCardDataWithApiKeyQuery();
  const [checkUserApiKeys] = useCheckUserApiKeysMutation();

  const { getUserData } = useSeoApi();

  const refreshUserDataBasics = async (options?: RefreshOptions): Promise<void> => {
    try {
      const me = await fetchMe().unwrap();
      const data = (await getUserData()) as {
        email: string;
        phone: string;
        reg_date: string;
        account_count: number;
        sales_account_count: number;
        account_weekly_product_order_sum: number;
        active_module_ab: boolean;
        expiration_date_module_ab: string | false;
      };

      dispatch(
        setUser({
          ...user,
          permissions: me.permissions,
          email: data.email,
          phone: data.phone,
          registrationDate: data.reg_date,
          isLoadingUserBasicData: false,
          stores: data.account_count,
          salesStores: data.sales_account_count,
          weeklyOrderSum: data.account_weekly_product_order_sum,
          isActiveAB: data.active_module_ab,
          expirationABDate: data.expiration_date_module_ab,
        }),
      );

      if (options?.refetchApiKeys) {
        const updatedKeys = await fetchApiKeys().unwrap();

        let finalKeys = updatedKeys;

        if (options.productId) {
          try {
            const checkResult = await checkUserApiKeys({
              art: options.productId,
            }).unwrap();

            finalKeys = updatedKeys.map((apiKey) => {
              const check = checkResult.find((c) => c.id === apiKey.id);

              return {
                ...apiKey,
                error: check?.error ?? false,
                success: check?.success ?? true,
              };
            });
          } catch (e) {
            console.error('Ошибка при проверке WB ключей', e);
          }
        }

        // @ts-ignore TODO: https://jira.marpla.ru/browse/MP-913
        dispatch(setApiKeys(finalKeys));
      }

      if (options?.refetchCard && options.productId) {
        if (options.isCorrectApiKeys) {
          await fetchCardDataWithApiKey({ productId: +options.productId });
        } else {
          await fetchCardData({ productId: options.productId });
        }
      }
    } catch (error) {
      console.error('Ошибка при обновлении данных пользователя:', error);
    }
  };

  return refreshUserDataBasics;
};
